// M004-F0634 §0a.2 — the ONE-list side drawer.
//
// The same side-drawer pattern as §4 (UI-4: the BookingDrawer shell —
// backdrop + aside, role="dialog", outside-click dismiss, Close control;
// NO modal, NO wizard, NO window.prompt), plain words first:
//   - Full history in plain words: the §5d.4.1 HistoryEntry list
//     (who / what / when / amount for every event) — the server's admin
//     words rendered verbatim, in order.
//   - Price basis in words: the server-computed price_basis, not a quote id.
//   - Actions by state, gated by the SERVER-computed can flags (the client
//     keeps no state machine of its own): Approve / Reject with reason
//     (inline reason field, validated before the call) / Reprice via the
//     pricing control (plan ▾ | Custom £) / Retry.
//   - Problems rows state the fix inline: problem.text plus problem.fix as
//     an inline fix link. Raw codes appear ONLY under the collapsed
//     "Details for support" disclosure.
//   - Errors render the API's admin-readable message verbatim — never a
//     bare code, never an alert.
//
// Step-up: a 401 STEP_UP_REQUIRED on any action opens StepUpDialog and
// replays THE SAME action with the SAME body after success — when the wire
// carries the challenge (getStepUpChallengeFromError). The §5d.4.1 surface
// as merged emits a BARE {code,message} 401 with NO challenge; that arm
// renders the server's message verbatim instead of a dialog it cannot
// satisfy (Core follow-up — see the MR description).
//
// Reprice note: the §5d.4.1 wire carries NO booking reference (the schema's
// additionalProperties:false set), so the ordinary pricing write
// (…/pricing/bookings/:bookingId/price) has no target from this surface.
// The control renders only when an item DOES carry booking_id (Core may
// add the field additively — no client change needed then); until that
// lands, a can.reprice item shows the honest disabled state instead.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import StepUpDialog from '../auth/StepUpDialog';
import TxChip from '../TxChip';
import BillingStageLines from './BillingStageLines';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import { billingApi, pricingApi } from '../../services/api';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';
import {
  fixTargetFor,
  formatItemWhen,
  formatHistoryAt,
  formatMinorAmount,
  handOffIdFor,
  settledSentence,
  billingStageView,
  isBareStepUpRequired,
  isSettledItem,
  ledgerHrefFor,
  studioTabHref,
  supportDetailLines,
} from './billingPipelineModel';
import './BillingDrawer.css';

export const BILLPIPE_DRAWER_TESTID = 'billing-drawer';

// §5d.4.1 — reject reason: required, 1..500, validated BEFORE the call.
export const validateDecisionReason = (value) => {
  const trimmed = String(value ?? '').trim();
  if (trimmed.length < 1) return 'A reason is required (1–500 characters).';
  if (trimmed.length > 500) return 'A reason is required (1–500 characters).';
  return null;
};

const majorStringToMinor = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const minor = Math.round(parseFloat(trimmed) * 100);
  return Number.isInteger(minor) && minor > 0 ? minor : null;
};

const BillingDrawer = ({
  item,
  personName,
  organisationId,
  onClose,
  refetchItem,
}) => {
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyError, setHistoryError] = useState('');
  // M004-F0716 row 15: "no events" is only ever said for a read that SUCCEEDED with zero entries, never while loading.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [actionError, setActionError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const [errorCodes, setErrorCodes] = useState([]);
  // The EXACT thunk that hit 401 STEP_UP_REQUIRED — replayed unchanged.
  const pendingReplayRef = useRef(null);
  // Closes the async race: a fetch in flight when the drawer is dismissed
  // must never write state into the unmounted drawer.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const [itemOverride, setItemOverride] = useState(null);

  const [planChoices, setPlanChoices] = useState([]);
  const [selectedChoice, setSelectedChoice] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [pricePreview, setPricePreview] = useState(null);
  const [pricePreviewError, setPricePreviewError] = useState('');
  const [priceError, setPriceError] = useState('');

  const dismissHandlers = useDismissOnOutsideClick(onClose);
  const currentItem = itemOverride || item;

  const loadHistory = useCallback(async () => {
    if (!currentItem?.id || !organisationId) return;
    try {
      const response = await billingApi.getItemHistory(organisationId, currentItem.id);
      if (!mountedRef.current) return;
      const entries = Array.isArray(response?.data) ? response.data : [];
      setHistoryEntries(entries);
      setHistoryError('');
      setHistoryLoaded(true);
    } catch (error) {
      if (!mountedRef.current) return;
      setHistoryError(error?.response?.data?.message || error?.message || 'Could not load the history.');
    }
  }, [organisationId, currentItem?.id]);

  useEffect(() => {
    setActionError('');
    setReason('');
    setReasonError('');
    setItemOverride(null);
    setSelectedChoice('');
    setCustomAmount('');
    setPricePreview(null);
    setPricePreviewError('');
    setPriceError('');
    setErrorCodes([]);
    setHistoryLoaded(false);
  }, [item?.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const recordErrorCode = (code) => {
    if (code) setErrorCodes((current) => (current.includes(code) ? current : [...current, code]));
  };

  // After a successful action: re-read the view. The item still in it →
  // render the fresh row; gone from it (approved, rejected, fixed) → the
  // drawer closes — the item has nothing left to say on this view.
  const refreshAfterAction = useCallback(async () => {
    if (refetchItem) {
      const fresh = await refetchItem();
      if (!mountedRef.current) return;
      if (fresh) {
        setItemOverride(fresh);
        await loadHistory();
        return;
      }
      onClose();
      return;
    }
    await loadHistory();
  }, [refetchItem, loadHistory, onClose]);

  const runAction = (name, thunk, onError) => {
    setBusyAction(name);
    setActionError('');
    Promise.resolve()
      .then(thunk)
      .then(async () => {
        pendingReplayRef.current = null;
        await refreshAfterAction();
      })
      .catch((error) => {
        const challenge = getStepUpChallengeFromError(error);
        if (challenge?.id) {
          // The challenge-bearing arm: keep the EXACT thunk and re-run it
          // unchanged after the dialog completes — the same method, the
          // same body, never rebuilt.
          pendingReplayRef.current = () => runAction(name, thunk, onError);
          setStepUpChallenge(challenge);
          return;
        }
        if (onError) {
          // The control's own error slot (the pricing write's C6 arm —
          // the message verbatim in the control, not the drawer body).
          recordErrorCode(error?.response?.data?.code);
          onError(error);
          return;
        }
        if (isBareStepUpRequired(error)) {
          recordErrorCode('STEP_UP_REQUIRED');
          setActionError(error?.response?.data?.message || 'A fresh second factor is required.');
          return;
        }
        recordErrorCode(error?.response?.data?.code);
        setActionError(error?.response?.data?.message || error?.message || 'The request failed.');
      })
      .finally(() => {
        if (mountedRef.current) setBusyAction('');
      });
  };

  // §0a.1 — the drawer's Approve goes through the ONE bulk endpoint with
  // exactly this item and the version read with it (DC-3b).
  const approve = () => {
    runAction('approve',
      () => billingApi.approveItems(organisationId, [{ id: currentItem.id, version: currentItem.version }]));
  };

  const reject = () => {
    const validation = validateDecisionReason(reason);
    setReasonError(validation);
    if (validation) return; // validated BEFORE the call — no request on an invalid reason
    runAction('reject',
      () => billingApi.rejectItem(organisationId, currentItem.id, reason.trim()));
  };

  const retry = () => {
    runAction('retry', () => billingApi.retryItem(organisationId, currentItem.id));
  };

  // §0a.2 — the pricing control (the server's can.reprice gates it): the
  // org's eligible plans, the Custom £ field, the live quote previewed
  // BEFORE Save (a server preview, never local rate math).
  useEffect(() => {
    let current = true;
    if (!organisationId || isSettledItem(currentItem) || currentItem?.can?.reprice !== true) return () => { current = false; };
    pricingApi.listPlans(organisationId)
      .then((response) => {
        if (!current) return;
        const body = response?.data?.data ?? response?.data ?? {};
        const all = Array.isArray(body.plans) ? body.plans : [];
        const eligible = all
          .filter((candidate) => (candidate.status || 'active') === 'active' && !candidate.is_template)
          .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
        setPlanChoices(eligible);
      })
      .catch(() => { if (current) setPlanChoices([]); });
    return () => { current = false; };
  }, [organisationId, currentItem?.can?.reprice, currentItem?.status]);

  const handlePriceSelect = async (value) => {
    setSelectedChoice(value);
    setPricePreview(null);
    setPricePreviewError('');
    setPriceError('');
    if (value === '' || value === 'custom') return;
    try {
      const response = await pricingApi.getBookingQuotePreview(
        organisationId, currentItem.booking_id, Number(value),
      );
      const body = response?.data?.data ?? response?.data ?? null;
      setPricePreview(body?.preview ?? null);
    } catch (error) {
      setPricePreview(null);
      const code = error?.response?.data?.error || error?.response?.status;
      setPricePreviewError(code != null ? String(code) : 'unavailable');
    }
  };

  const savePrice = () => {
    const body = {};
    if (selectedChoice === 'custom') {
      const minor = majorStringToMinor(customAmount);
      if (minor === null) {
        setPriceError('Enter a custom price of more than £0.00 (up to two decimal places).');
        return;
      }
      body.custom_price_minor = minor;
    } else if (selectedChoice !== '') {
      body.pricing_plan_id = Number(selectedChoice);
    } else {
      return;
    }
    runAction('savePrice',
      () => pricingApi.setBookingPrice(organisationId, currentItem.booking_id, body),
      (error) => {
        setPriceError(error?.response?.data?.message || error?.message || 'Failed to save the price.');
      });
  };

  if (!item) return null;

  // M004-F0675: a settled (handed-off) item is READ-ONLY in Core — the
  // Ledger owns it. Settled is a property of the row, not of the can flags:
  // no control is rendered even if every flag were true.
  const settled = isSettledItem(currentItem || item);
  // M004-F0716 phase B: the stage of the freshest read of the item (the drawer re-reads it), Core's status_text only as the fallback.
  const stageView = billingStageView(currentItem || item);
  const handOffId = handOffIdFor(currentItem || item);
  const can = settled ? {} : (currentItem?.can || item.can || {});
  // The reprice control needs a booking to write against; the §5d.4.1 wire
  // does not carry one (see the header). When Core adds it additively the
  // control comes alive with no client change.
  const repriceTargetAvailable = currentItem?.booking_id != null || item?.booking_id != null;
  const showPricingControl = !settled && can.reprice === true;
  const problem = settled ? null : (currentItem?.problem ?? item.problem);
  const fix = fixTargetFor(currentItem || item);
  const supportLines = supportDetailLines(currentItem || item, errorCodes);

  return (
    <div className="bkh-drawer-backdrop" role="presentation" {...dismissHandlers}>
      <aside
        className="bkh-drawer billpipe-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Billing — ${item.booking_title}`}
        data-testid={BILLPIPE_DRAWER_TESTID}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bkh-drawer-header">
          <div>
            <h2 id="billing-drawer-title">{item.booking_title}</h2>
            <p>{personName || '—'}</p>
            {/* M004-F0712 S2: the raw "Ledger reference" UUID is gone (a UUID is the database speaking); the TX chip is the reference. */}
            <TxChip handOffId={handOffId} txId={(currentItem || item).tx_id} orgId={organisationId} customerUserId={(currentItem || item).user_id} expected />
          </div>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
        </div>

        <dl className="bkh-drawer-list" data-testid="billing-drawer-facts">
          <div>
            <dt>Status</dt>
            <dd>
              <span className={`billpipe-badge${stageView.problem ? ' billpipe-badge-problem' : ''}`} data-testid="billing-drawer-status" data-stage={stageView.stage || undefined}>{stageView.text}</span>
              <BillingStageLines view={stageView} testidPrefix="billing-drawer" />
            </dd>
          </div>
          <div>
            <dt>Room</dt>
            <dd>{item.room}</dd>
          </div>
          <div>
            <dt>When</dt>
            <dd>{formatItemWhen(item)}</dd>
          </div>
          {settled ? null : (
            <>
              <div>
                <dt>Amount</dt>
                <dd data-testid="billing-drawer-amount">
                  {formatMinorAmount(item.amount_minor, item.currency)}
                </dd>
              </div>
              <div>
                <dt>Price basis</dt>
                <dd data-testid="billing-drawer-price-basis">{item.price_basis}</dd>
              </div>
            </>
          )}
        </dl>

        {settled ? (
          <section className="billpipe-drawer-section" data-testid="billing-drawer-settled">
            <h3>Where this stands</h3>
            {/* M004-F0716 (design 5.2 row 14): Core maps posted, handed_over and charged ALL to 'billed', so the sentence is Core's
                own status_text, verbatim. "Cannot be changed here" is added only for a text that says the Ledger has it. */}
            <p data-testid="billing-drawer-settled-text">
              {settledSentence(currentItem || item)}
            </p>
            <a
              href={ledgerHrefFor(organisationId)}
              data-testid="billing-drawer-ledger-link"
              onClick={(event) => {
                event.preventDefault();
                window.location.assign(ledgerHrefFor(organisationId));
              }}
            >
              Open in the Ledger
            </a>
          </section>
        ) : null}

        {problem ? (
          <section className="billpipe-drawer-section" data-testid="billing-drawer-problem">
            <h3>Problem</h3>
            <p data-testid="billing-drawer-problem-text">{problem.text}</p>
            {fix.kind === 'studio' ? (
              <a
                href={studioTabHref(organisationId)}
                data-testid="billing-drawer-problem-fix"
                onClick={(event) => {
                  event.preventDefault();
                  window.location.assign(studioTabHref(organisationId));
                }}
              >
                {fix.label}
              </a>
            ) : null}
            {fix.kind === 'text' ? (
              <p className="billpipe-muted" data-testid="billing-drawer-problem-fix-text">{problem.fix}</p>
            ) : null}
            {/* fix.kind === 'drawer': the pricing control below IS the fix
                for booking-pricing-control, and the Retry button for
                retry-dispatch — no second link needed. */}
          </section>
        ) : null}

        {showPricingControl && repriceTargetAvailable ? (
          <section className="billpipe-drawer-section" data-testid="billing-price-control">
            <h3>Price</h3>
            <div className="billpipe-price-control">
              <label htmlFor="billing-price-select">Price plan</label>
              <select
                id="billing-price-select"
                data-testid="billing-price-select"
                value={selectedChoice}
                onChange={(event) => handlePriceSelect(event.target.value)}
                disabled={busyAction === 'savePrice'}
              >
                <option value="">Choose a plan…</option>
                {planChoices.map((choice) => (
                  <option key={choice.id} value={String(choice.id)}>{choice.name}</option>
                ))}
                <option value="custom">Custom price…</option>
              </select>
              {selectedChoice === 'custom' ? (
                <input
                  type="number"
                  data-testid="billing-price-custom-amount"
                  aria-label="Custom price (£)"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={customAmount}
                  onChange={(event) => { setCustomAmount(event.target.value); setPriceError(''); }}
                  disabled={busyAction === 'savePrice'}
                />
              ) : null}
              {selectedChoice === 'custom' && majorStringToMinor(customAmount) !== null ? (
                <p className="billpipe-price-preview" data-testid="billing-price-preview">
                  {'Not applied yet — Custom price: '}
                  <strong>{formatMinorAmount(majorStringToMinor(customAmount), item.currency)}</strong>
                </p>
              ) : null}
              {selectedChoice !== '' && selectedChoice !== 'custom' && pricePreview ? (
                <p className="billpipe-price-preview" data-testid="billing-price-preview">
                  {`Not applied yet — ${pricePreview.plan_name || 'Selected plan'}: `}
                  <strong data-testid="billing-price-preview-amount">
                    {formatMinorAmount(pricePreview.amount_minor, pricePreview.currency)}
                  </strong>
                </p>
              ) : null}
              {pricePreviewError ? (
                <p className="billpipe-error" data-testid="billing-price-preview-error">
                  {`Price preview unavailable (${pricePreviewError}). Save will still validate the plan.`}
                </p>
              ) : null}
              <button
                type="button"
                className="btn btn-outline"
                data-testid="billing-price-save"
                onClick={savePrice}
                disabled={busyAction === 'savePrice' || selectedChoice === ''
                  || (selectedChoice === 'custom' && customAmount.trim() === '')}
              >
                {busyAction === 'savePrice' ? 'Saving…' : 'Save'}
              </button>
              {priceError ? (
                <p className="billpipe-error" role="alert" data-testid="billing-price-error">{priceError}</p>
              ) : null}
            </div>
          </section>
        ) : null}
        {showPricingControl && !repriceTargetAvailable ? (
          <p className="billpipe-muted" data-testid="billing-reprice-unavailable">
            Reprice is not available for this item yet — it does not expose its booking.
          </p>
        ) : null}

        {can.approve || can.reject ? (
          <section className="billpipe-drawer-section" data-testid="billing-drawer-actions">
            <h3>Check</h3>
            {can.approve ? (
              <button
                type="button"
                className="btn btn-success"
                data-testid="billing-approve"
                onClick={approve}
                disabled={Boolean(busyAction)}
              >
                {busyAction === 'approve' ? 'Approving…' : 'Approve'}
              </button>
            ) : null}
            {can.reject ? (
              <>
                <label htmlFor="billing-reason" className="billpipe-reason-label">
                  Reason (required to reject)
                </label>
                <textarea
                  id="billing-reason"
                  className="billpipe-reason"
                  data-testid="billing-reason"
                  rows="2"
                  maxLength={600}
                  value={reason}
                  onChange={(event) => { setReason(event.target.value); setReasonError(''); }}
                  placeholder="Why this item is being rejected"
                />
                {reasonError ? (
                  <p className="billpipe-error" role="alert" data-testid="billing-reason-error">{reasonError}</p>
                ) : null}
                <button
                  type="button"
                  className="btn btn-danger"
                  data-testid="billing-reject"
                  onClick={reject}
                  disabled={Boolean(busyAction)}
                >
                  {busyAction === 'reject' ? 'Rejecting…' : 'Reject'}
                </button>
              </>
            ) : null}
          </section>
        ) : null}

        {can.retry ? (
          <section className="billpipe-drawer-section" data-testid="billing-drawer-retry">
            <button
              type="button"
              className="btn btn-outline"
              data-testid="billing-retry"
              onClick={retry}
              disabled={Boolean(busyAction)}
            >
              {busyAction === 'retry' ? 'Retrying…' : 'Retry'}
            </button>
          </section>
        ) : null}

        {actionError ? (
          <p className="billpipe-error" role="alert" data-testid="billing-action-error">{actionError}</p>
        ) : null}

        <section className="billpipe-drawer-section" data-testid="billing-drawer-history">
          <h3>History</h3>
          {historyError ? (
            <p className="billpipe-error" role="alert" data-testid="billing-history-error">{historyError}</p>
          ) : null}
          {!historyError && !historyLoaded && historyEntries.length === 0 ? (
            <p className="billpipe-muted" role="status" data-testid="billing-history-loading">Loading the history…</p>
          ) : !historyError && historyEntries.length === 0 ? (
            <p className="billpipe-muted" data-testid="billing-history-empty">No events are recorded for this item.</p>
          ) : (
            <ol className="billpipe-history-list" data-testid="billing-history-list">
              {historyEntries.map((entry, index) => (
                <li key={index} data-testid="billing-history-event" className="billpipe-history-event">
                  <span className="billpipe-history-head">
                    {entry.what}
                    {entry.who ? ` — ${entry.who}` : ''}
                    {entry.at ? ` at ${formatHistoryAt(entry.at, currentItem?.timezone)}` : ''}
                  </span>
                  {entry.amount && !settled ? (
                    <span className="billpipe-history-details">{entry.amount}</span>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </section>

        <details className="billpipe-support" data-testid="billing-support-details">
          <summary>Details for support</summary>
          <ul>
            {supportLines.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </details>

        <StepUpDialog
          challenge={stepUpChallenge}
          title="Verify billing decision"
          description="Confirm your identity before this billing action is applied."
          onComplete={() => {
            setStepUpChallenge(null);
            const replay = pendingReplayRef.current;
            pendingReplayRef.current = null;
            if (replay) replay();
          }}
          onCancel={() => {
            setStepUpChallenge(null);
            pendingReplayRef.current = null;
            setActionError('Verification was cancelled — the action was not applied.');
          }}
        />
      </aside>
    </div>
  );
};

export default BillingDrawer;
